import type { TFunction } from 'i18next';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import type { ApiError } from '@/lib/agent.types';
import { toInterpolationValues, translateErrorMessage } from './error-formatter';

/**
 * Set validation errors from API response on react-hook-form fields
 *
 * Maps backend validation errors to form field errors with translated messages.
 * Backend returns errors in format: { fieldName: ["error.key1", "error.key2"] }
 *
 * @param errors - Validation errors object from ApiError
 * @param setError - setError function from react-hook-form
 * @param t - Translation function from i18next
 * @param metadata - Optional metadata for error message interpolation (e.g., {abbreviation: "HR"})
 *
 * @example
 * ```tsx
 * catch (error) {
 *   if (isValidationError(error)) {
 *     setFormValidationErrors(error.errors, setError, t, error.metadata);
 *   }
 * }
 * ```
 */
export function setFormValidationErrors<TFieldValues extends FieldValues>(
  errors: Record<string, string[]> | undefined,
  setError: UseFormSetError<TFieldValues>,
  t: TFunction,
  metadata?: ApiError['metadata']
): void {
  if (!errors) {
    return;
  }

  Object.entries(errors).forEach(([fieldName, errorKeys]) => {
    if (errorKeys.length === 0) return;

    // Take the first error for the field
    const errorKey = errorKeys[0];
    const message = translateFieldError(errorKey, t, metadata);

    // Set the error on the form field
    setError(fieldName as Path<TFieldValues>, {
      type: 'server',
      message
    });
  });
}

/**
 * Translate a field validation error key
 *
 * Handles i18n keys from backend or returns the message as is.
 * Supports metadata interpolation for dynamic values in error messages.
 *
 * @param errorKey - Error key or message from backend
 * @param t - Translation function from i18next
 * @param metadata - Optional metadata for interpolation (e.g., {abbreviation: "HR"})
 * @returns Translated error message with interpolated values
 *
 * @example
 * ```ts
 * const message = translateFieldError('errors.department.duplicateAbbreviation', t, {abbreviation: 'HR'});
 * // Returns: "Department with abbreviation 'HR' already exists"
 * ```
 */
export function translateFieldError(
  errorKey: string,
  t: TFunction,
  metadata?: ApiError['metadata']
): string {
  const interpolationValues = toInterpolationValues(metadata);
  // If it looks like an i18n key (contains dots, no spaces)
  if (errorKey.includes('.') && !errorKey.includes(' ')) {
    const translated = t(errorKey, interpolationValues);
    // If translation exists, use it
    if (translated !== errorKey) {
      return translated;
    }
  }

  // If it starts with known validation prefixes, try to map it
  if (errorKey.startsWith('errors.validation.')) {
    const translated = t(errorKey, interpolationValues);
    if (translated !== errorKey) {
      return translated;
    }
  }

  // Otherwise return as is (might already be a user-friendly message)
  return errorKey;
}

/**
 * Get error message for a specific field from validation errors object
 *
 * @param fieldName - Name of the field
 * @param errors - Validation errors object from ApiError
 * @param t - Translation function
 * @param metadata - Optional metadata for interpolation
 * @returns Translated error message or undefined if no error for field
 *
 * @example
 * ```tsx
 * const nameError = getFieldErrorMessage('name', error.errors, t, error.metadata);
 * if (nameError) {
 *   return <span className="error">{nameError}</span>;
 * }
 * ```
 */
export function getFieldErrorMessage(
  fieldName: string,
  errors: Record<string, string[]> | undefined,
  t: TFunction,
  metadata?: ApiError['metadata']
): string | undefined {
  if (!errors || !errors[fieldName] || errors[fieldName].length === 0) {
    return undefined;
  }

  const errorKey = errors[fieldName][0];
  return translateFieldError(errorKey, t, metadata);
}

/**
 * Narrow an ApiError's `errors` to the flat `{ field: string[] }` shape that
 * maps onto form fields. Empty or malformed values are rejected here and
 * surfaced as a root-level message instead.
 */
function toFieldErrorRecord(errors: unknown): Record<string, string[]> | null {
  if (!errors || typeof errors !== 'object') return null;

  const entries = Object.entries(errors as Record<string, unknown>).filter(
    (entry): entry is [string, string[]] =>
      Array.isArray(entry[1]) && entry[1].length > 0 && typeof entry[1][0] === 'string'
  );

  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

export interface ApplyApiErrorToFormOptions<TFieldValues extends FieldValues> {
  /**
   * Fields eligible for an inline error. Backend keys outside this list — e.g. a
   * nested Zod path like `user.email` that no rendered input is bound to — fall
   * through to the root message rather than silently vanishing. Omit to accept
   * every field the backend returns.
   */
  fields?: Path<TFieldValues>[];
  /** Field that receives the fallback message. Defaults to `root`. */
  rootField?: Path<TFieldValues>;
}

/**
 * Route an enriched ApiError onto a react-hook-form.
 *
 * Field-level validation errors attach inline to their inputs; everything else
 * (network error, 5xx, a Zod schema failure, or a field the form doesn't
 * render) collapses to a single translated root message. Prefer this over
 * pairing {@link setFormValidationErrors} with a hand-written root fallback at
 * each call site.
 */
export function applyApiErrorToForm<TFieldValues extends FieldValues>(
  apiError: ApiError,
  setError: UseFormSetError<TFieldValues>,
  t: TFunction,
  options: ApplyApiErrorToFormOptions<TFieldValues> = {}
): void {
  const { fields, rootField = 'root' as Path<TFieldValues> } = options;
  const record = toFieldErrorRecord(apiError.errors);
  let applied = false;
  const unrouted: string[] = [];

  if (record) {
    const eligible = fields && new Set<string>(fields);
    for (const [field, errorKeys] of Object.entries(record)) {
      if (!errorKeys?.length) continue;
      const message = translateFieldError(errorKeys[0], t, apiError.metadata);
      if (eligible && !eligible.has(field)) {
        unrouted.push(message);
        continue;
      }
      setError(field as Path<TFieldValues>, { type: 'server', message });
      applied = true;
    }
  }

  if (!applied || unrouted.length > 0) {
    setError(rootField, {
      type: 'server',
      // Once something landed inline, the generic message would contradict it —
      // the root alert carries the messages that found no input instead.
      message: applied ? unrouted.join(' ') : translateErrorMessage(apiError, t)
    });
  }
}
